/* eslint-disable unicorn/no-await-expression-member */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import {
  type PaymentMethod,
  type StripePaymentElementChangeEvent,
} from '@stripe/stripe-js';
import { capitalizeFirstLetter, type IconName } from '@sg/garnish';

import type { UseStripePaymentProps } from '../../types';
import {
  getCreditCardIcon,
  removeEndingIn,
  useLabelAndIconByType,
} from './helpers';

export const useStripePayment = (props: UseStripePaymentProps) => {
  const { isIntentConfirmationEnabled, paymentMethods, stripeIntentConfig } =
    props;

  // ─── Stripe ────────────────────────────────────────────────────────────────

  const stripe = useStripe();
  const elements = useElements();

  // ─── State ─────────────────────────────────────────────────────────────────

  const [createdPaymentMethod, setCreatedPaymentMethod] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [selectedPaymentIcon, setSelectedPaymentIcon] =
    useState<IconName>('IconCreditCard');

  const isGooglePay = selectedPaymentMethod === GOOGLE_PAY;
  const isApplePay = selectedPaymentMethod === APPLE_PAY;
  const isLink = selectedPaymentMethod === LINK;
  const isCashApp = selectedPaymentMethod === CASH_APP;

  // ─── Payment Method Creation ───────────────────────────────────────────────

  const createPaymentMethod = useCallback(
    async (shouldSubmit = false) => {
      if (!stripe || !elements) {
        throw new Error('Stripe not initialized');
      }

      if (shouldSubmit) {
        const { error: submitError } = await elements.submit();

        if (submitError) {
          throw new Error(submitError.message);
        }
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        elements,
      });

      if (error) {
        throw new Error(error.message);
      }

      setCreatedPaymentMethod(paymentMethod.id);

      return paymentMethod;
    },
    [elements, stripe],
  );

  // ─── Handle Payment Change ─────────────────────────────────────────────────

  const convertTypeToLabelAndIcon = useLabelAndIconByType();
  const handlePaymentElementChange = useMemo(
    () =>
      getPaymentMethodChangeHandler({
        paymentMethods,
        convertTypeToLabelAndIcon,
        setCreatedPaymentMethod,
        setSelectedPaymentMethod,
        setSelectedPaymentIcon,
        createPaymentMethod,
      }),
    [convertTypeToLabelAndIcon, createPaymentMethod, paymentMethods],
  );

  useEffect(() => {
    if (!stripe || !elements) {
      return;
    }

    const paymentElement = elements.getElement('payment');

    if (!paymentElement) {
      return;
    }

    paymentElement.on('change', handlePaymentElementChange);

    return () => {
      paymentElement.off('change', handlePaymentElementChange);
    };
  }, [elements, stripe, handlePaymentElementChange]);

  // ─── Confirm Callback ──────────────────────────────────────────────────────

  const confirmStripe = useCallback(async () => {
    try {
      if (!stripe || !elements) {
        return { status: 'failed', error: 'Stripe not initialized' };
      }

      const { error: submitError, selectedPaymentMethod } =
        await elements.submit();

      if (submitError) {
        return { status: 'failed', error: submitError.message };
      }

      const paymentMethodId = createdPaymentMethod || (await createPaymentMethod())?.id; // prettier-ignore

      if (isIntentConfirmationEnabled) {
        // Maps the stripe-react-native interface to web.
        return await new Promise((resolve) => {
          stripeIntentConfig?.confirmHandler?.(
            {
              id: paymentMethodId,
              paymentMethodType: selectedPaymentMethod,
            } as never,
            false,
            async ({ clientSecret = '' }) => {
              const result = await stripe.confirmPayment({
                elements,
                clientSecret,
                redirect: redirectCondition,
                confirmParams: { return_url: defaultRedirect },
              });

              if (result.error) {
                resolve({ status: 'failed', error: result.error.message });
                return;
              }

              resolve({ status: 'completed' });
            },
          );
        });
      }

      if (!paymentMethodId) {
        return { status: 'failed', error: 'No payment method created' };
      }

      return { status: 'completed', paymentMethodId };
    } catch (error) {
      return { status: 'failed', error: (error as Error).message };
    }
  }, [
    stripe,
    elements,
    isIntentConfirmationEnabled,
    createdPaymentMethod,
    createPaymentMethod,
    stripeIntentConfig,
  ]);

  return {
    isGooglePay,
    isApplePay,
    isLink,
    isCashApp,
    selectedPaymentMethod,
    selectedPaymentIcon,
    confirmStripe,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Handles changes to the payment method, setting state as needed.
 */
const getPaymentMethodChangeHandler = (params: PaymentMethodChangeHandler) => {
  const {
    paymentMethods,
    convertTypeToLabelAndIcon,
    setCreatedPaymentMethod,
    setSelectedPaymentMethod,
    setSelectedPaymentIcon,
    createPaymentMethod,
  } = params;

  return async (event: StripePaymentElementChangeEvent) => {
    try {
      setCreatedPaymentMethod('');

      const id = event.value.payment_method?.id;
      const savedCard = paymentMethods.find((pm) => pm.id === id);

      // Customer selected a card that is already saved in Stripe.
      if (savedCard?.description && savedCard?.cardType) {
        setSelectedPaymentMethod(removeEndingIn(savedCard.description));
        setSelectedPaymentIcon(getCreditCardIcon(savedCard.cardType));
        return;
      }

      // Customer is adding a new card.
      if (event.value.type === 'card' && event.complete) {
        const paymentMethod = await createPaymentMethod(true);

        if (!paymentMethod?.card) return;

        const { brand, last4 } = paymentMethod.card;

        const label = `${capitalizeFirstLetter(brand)} ${last4}`;

        setSelectedPaymentMethod(label);
        setSelectedPaymentIcon(getCreditCardIcon(brand.toUpperCase()));

        return;
      }

      // Customer selected a wallet payment type.
      if (event.value.type) {
        const { label, icon } = convertTypeToLabelAndIcon(event.value.type);
        setSelectedPaymentMethod(label);
        setSelectedPaymentIcon(icon);
      }
    } catch (error) {
      throw new Error((error as Error).message);
    }
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type PaymentMethodChangeHandler = {
  paymentMethods: UseStripePaymentProps['paymentMethods'];
  convertTypeToLabelAndIcon: ReturnType<typeof useLabelAndIconByType>;
  setCreatedPaymentMethod: (value: string) => void;
  setSelectedPaymentMethod: (value: string) => void;
  setSelectedPaymentIcon: (value: IconName) => void;
  createPaymentMethod: (
    shouldSubmit?: boolean,
  ) => Promise<PaymentMethod | undefined>;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const GOOGLE_PAY = 'Google Pay';
const APPLE_PAY = 'Apple Pay';
const LINK = 'Link';
const CASH_APP = 'Cash App';
const redirectCondition = 'if_required' as const;
const defaultRedirect = window.location.origin;
